/**
 * Chart colours come from the design tokens (SPEC §17.4). The main thread reads the CSS variables
 * (`readThemeFromCss`) and posts the strings to the render worker, which parses them to linear
 * RGBA floats for the shaders.
 */

export type RGBA = readonly [number, number, number, number];

/** Colour token strings as read from CSS (any `#rgb`, `#rrggbb`, `#rrggbbaa`, `rgb()`/`rgba()`). */
export type ColorTokens = {
  bg: string;
  panel: string;
  grid: string;
  text: string;
  textDim: string;
  up: string;
  down: string;
  last: string;
  accent: string;
  /** Warning / caution (orange in every scheme): the position tag's SL chip. */
  warn: string;
};

/**
 * What the main thread posts to the engine: the colours plus the font stack for canvas text
 * (`--font-num`, Rubik per ADR-0008). `font` absent = `DEFAULT_FONT_FAMILY`.
 */
export type ThemeTokens = ColorTokens & { font?: string };

export type ChartTheme = { [K in keyof ColorTokens]: RGBA };

/** Mirrors `fontFamily.num` in @helm/design (the `--font-num` token). */
export const DEFAULT_FONT_FAMILY = '"Rubik Variable", "Rubik", "Inter Variable", system-ui, sans-serif';
/** CSS variable backing the chart's text font. */
export const FONT_VAR = "--font-num";

/** Dark-theme defaults (Tradesea chart palette, DESIGN-REFERENCE) for when CSS is unavailable. */
export const DEFAULT_TOKENS: ColorTokens = {
  bg: "#131314",
  panel: "#18191B",
  grid: "#292A2C",
  text: "#7F838B",
  textDim: "#5A5D63",
  up: "#0B9981",
  down: "#F23645",
  last: "#FAFAFA",
  accent: "#1858FF",
  warn: "#F79009",
};

/** CSS variable backing each token. */
export const TOKEN_VARS: Record<keyof ColorTokens, string> = {
  bg: "--bg-1",
  panel: "--bg-2",
  /** Grid, axis borders and pane separators (Tradesea chart axis #292A2C). */
  grid: "--chart-grid",
  /** Axis labels and legend (Tradesea chart text #7F838B). */
  text: "--chart-text",
  textDim: "--text-3",
  up: "--buy",
  down: "--sell",
  last: "--last",
  accent: "--accent",
  warn: "--warn",
};

export function parseColor(css: string, fallback: RGBA = [1, 0, 1, 1]): RGBA {
  const s = css.trim().toLowerCase();
  if (s.startsWith("#")) {
    const h = s.slice(1);
    const exp = h.length === 3 || h.length === 4 ? h.replace(/./g, (c) => c + c) : h;
    if (!/^[0-9a-f]{6}([0-9a-f]{2})?$/.test(exp)) return fallback;
    const n = (i: number): number => parseInt(exp.slice(i, i + 2), 16) / 255;
    return [n(0), n(2), n(4), exp.length === 8 ? n(6) : 1];
  }
  const m = /^rgba?\(([^)]+)\)$/.exec(s);
  if (m?.[1]) {
    const parts = m[1].split(/[\s,/]+/).filter((p) => p.length > 0);
    const ch = (p: string | undefined): number => {
      if (p === undefined) return Number.NaN;
      return p.endsWith("%") ? parseFloat(p) / 100 : parseFloat(p) / 255;
    };
    const a =
      parts[3] === undefined ? 1 : parts[3].endsWith("%") ? parseFloat(parts[3]) / 100 : parseFloat(parts[3]);
    const out: RGBA = [ch(parts[0]), ch(parts[1]), ch(parts[2]), a];
    return out.every((v) => Number.isFinite(v)) ? out : fallback;
  }
  return fallback;
}

export function resolveTheme(tokens: Partial<ThemeTokens>): ChartTheme {
  const out = {} as Record<keyof ColorTokens, RGBA>;
  for (const k of Object.keys(DEFAULT_TOKENS) as (keyof ColorTokens)[]) {
    const fb = parseColor(DEFAULT_TOKENS[k]);
    const v = tokens[k];
    out[k] = v ? parseColor(v, fb) : fb;
  }
  return out;
}

/** Main-thread helper: read token strings from computed CSS variables. */
export function readThemeFromCss(el: Element): ThemeTokens {
  const cs = getComputedStyle(el);
  const out: ThemeTokens = { ...DEFAULT_TOKENS };
  for (const k of Object.keys(TOKEN_VARS) as (keyof ColorTokens)[]) {
    const v = cs.getPropertyValue(TOKEN_VARS[k]).trim();
    if (v) out[k] = v;
  }
  const font = cs.getPropertyValue(FONT_VAR).trim();
  out.font = font || DEFAULT_FONT_FAMILY;
  return out;
}

export function withAlpha(c: RGBA, a: number): RGBA {
  return [c[0], c[1], c[2], c[3] * a];
}
